/*
   The account password policy, as the API enforces it.

   The backend's `StrongPassword` (schemas/auth.py) is the copy that protects accounts; this one
   exists so a person sees the rules while typing instead of meeting them one refusal at a time.
   Keep the two in step: the same four character classes, the same length bounds, counted the
   same way.

   It applies wherever a password is chosen (add user, accept invite, reset, change password) and
   never to sign-in: an account set before the rule existed still signs in with what it has.
*/

import { ApiError } from "@/lib/errors"

export const PASSWORD_MIN_LENGTH = 8
export const PASSWORD_MAX_LENGTH = 200

/**
 * The length at which a password that meets every rule reads as Strong rather than Good. Not a
 * rule: the API accepts 8. Length is what actually makes a password hard to guess, so the meter
 * rewards it.
 */
export const PASSWORD_STRONG_LENGTH = 12

/** The 422 `type` the API refuses a too-weak password with. */
export const WEAK_PASSWORD_ERROR = "password_too_weak"

export type PasswordRuleId = "length" | "uppercase" | "lowercase" | "number" | "special"

/** One line of the checklist. `id` is a policy rule's, or the screen's own for an extra one. */
export interface PasswordRequirement {
  id: string
  label: string
  met: boolean
}

export type PasswordStrengthLevel = "empty" | "weak" | "fair" | "good" | "strong"

export interface PasswordCheck {
  /** The policy's rules, in the order the checklist shows them. */
  requirements: PasswordRequirement[]
  /** Over the ceiling: refused however strong it is otherwise. */
  tooLong: boolean
  /** Every rule met and within the ceiling, so the API will accept it. */
  acceptable: boolean
  strength: PasswordStrengthLevel
}

/**
 * Length in code points, as Python's `len` counts it. `String.length` counts UTF-16 units, so an
 * emoji would count twice here and once there, and the two copies would disagree at the bounds.
 */
export function passwordLength(password: string): number {
  // Each surrogate pair is one code point: count it once.
  return password.replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]/g, "_").length
}

interface Rule {
  id: PasswordRuleId
  label: string
  test: (password: string) => boolean
}

// The Unicode classes match the backend's str methods: isupper, islower, isdecimal, and "not a
// letter, number or whitespace" for special. So "É" is an uppercase letter and a space is allowed
// but is not the special character.
const RULES: readonly Rule[] = [
  {
    id: "length",
    label: `At least ${String(PASSWORD_MIN_LENGTH)} characters`,
    test: (password) => passwordLength(password) >= PASSWORD_MIN_LENGTH,
  },
  { id: "uppercase", label: "An uppercase letter", test: (password) => /\p{Lu}/u.test(password) },
  { id: "lowercase", label: "A lowercase letter", test: (password) => /\p{Ll}/u.test(password) },
  { id: "number", label: "A number", test: (password) => /\p{Nd}/u.test(password) },
  {
    id: "special",
    label: "A special character",
    test: (password) => /[^\p{L}\p{N}\s]/u.test(password),
  },
]

export function checkPassword(password: string): PasswordCheck {
  const requirements = RULES.map((rule) => ({
    id: rule.id,
    label: rule.label,
    met: rule.test(password),
  }))
  const length = passwordLength(password)
  const metCount = requirements.filter((requirement) => requirement.met).length
  const tooLong = length > PASSWORD_MAX_LENGTH
  const acceptable = metCount === RULES.length && !tooLong
  return { requirements, tooLong, acceptable, strength: strengthOf(length, metCount, acceptable) }
}

function strengthOf(length: number, metCount: number, acceptable: boolean): PasswordStrengthLevel {
  if (length === 0) return "empty"
  // Good and Strong are only ever said about a password the API will take, so the meter can never
  // read green over a submit button that is still disabled.
  if (acceptable) return length >= PASSWORD_STRONG_LENGTH ? "strong" : "good"
  return metCount >= 3 ? "fair" : "weak"
}

/** A new password that may be submitted: acceptable, and typed the same twice. */
export function newPasswordReady(password: string, confirmation: string): boolean {
  return checkPassword(password).acceptable && confirmation === password
}

/**
 * The API's own sentence for a too-weak password, or null when the error is anything else.
 *
 * Read from the 422 body rather than `ApiError.message`, which prefixes the field name
 * ("new_password: Password must include …"): the message is shown as the API worded it, under a
 * field the person can already see.
 */
export function weakPasswordMessage(cause: unknown): string | null {
  if (!(cause instanceof ApiError) || cause.status !== 422) return null
  const detail = cause.body?.detail
  if (!Array.isArray(detail)) return null
  // Array.isArray narrows to any[]; read each entry as the unknown it is.
  const entries: readonly unknown[] = detail
  for (const entry of entries) {
    if (
      typeof entry === "object" &&
      entry !== null &&
      "type" in entry &&
      entry.type === WEAK_PASSWORD_ERROR &&
      "msg" in entry &&
      typeof entry.msg === "string"
    ) {
      return entry.msg
    }
  }
  return null
}
